import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tab, Tabs, useTheme } from '@mui/material';
import { GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { PageLayout } from '../../PageLayout';
import { TextBlock } from '../../Atoms/Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { PageCard } from '../../Compounds/CardWithTitle';
import { BenchmarkStatus } from '../../../store/BenchmarkStatus';
import {
  fetchPriceBenchmarks,
  getPriceBenchmarkLoadingSubject,
  getPriceBenchmarkSubject,
  PriceBenchMark
} from '../../../store/PriceBenchMark';
import {
  getDefaultPreferences,
  getSelectedAccountsSubject,
  getUser,
  getUserPreferencesSubject,
  GridPreferences,
  isInternalStaff,
  UserPreferencesContent
} from '../../../store/User';
import { TabPanel } from '../../Atoms/TabPanel';
import { Drawer } from '../../Atoms/Drawer';
import { DetailsDisplay } from '../../Compounds/DetailsDrawer/DetailsDisplay';
import { getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns } from '../../Compounds/UpstackDataGrid/helpers';
import {
  getBenchMarkColumnsByTab,
  PriceBenchmarkTabDisplayNames,
  PriceBenchmarkTabNames
} from './PriceBenchmarkGridColumnDefs';
import { MarketComparisonChart } from './MarketComparisonChart';
import { BenchmarkDetails } from './BenchmarkDetails';
import { InventoryAsset } from '../../../types';
import { getInventoryByIdSubject, getInventoryLoadingSubject, useInventory } from '../../../store/Inventory';
import { getTasks, contactAdvisor } from '../../../Api/Tasks/tasksApi';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { useProductsStore } from '../../../store/Product';
import { useOutsideInventorySchemasStore } from '../../../store/OutsideInventorySchema';
import { useSnackbar } from '../../../Context/SnackbarContext';

export interface TabProps {
  name: PriceBenchmarkTabNames;
  count: number;
}

export const CostIntelligencePage = () => {
  const defaultTabs: PriceBenchmarkTabNames[] = ['to_market', 'needs_review'];
  const [benchmarks, setBenchmarks] = useState<PriceBenchMark[]>([]);
  const [advisorContacted, setAdvisorContacted] = useState<boolean>(false);
  const [advisorLoading, setAdvisorLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [tabs, setTabs] = useState<TabProps[]>([]);
  const [allTabs, setAllTabs] = useState<TabProps[]>([]);
  const [benchmarksLoading, setBenchmarksLoading] = useState<boolean>(getPriceBenchmarkLoadingSubject().getValue());
  const [columns, setColumns] = useState<GridSingleSelectColDef[]>();
  const [search, setSearch] = useSearchParams();
  // On initial mount any assetDetailId in the URL is stale (reloaded/pasted link).
  // Block it synchronously so the drawer never opens and no fetch fires on first render.
  const mountedRef = useRef(false);
  const assetDetailId = mountedRef.current ? search.get('assetDetailId') : null;
  const [inventoryAsset, setInventoryAsset] = useState<InventoryAsset | null>(null);
  const [inventoryLoading, setInventoryLoading] = useState<boolean>(false);
  const [userPreferences, setUserPreferences] = useState<UserPreferencesContent>(getDefaultPreferences().content);
  const [existingTaskAssetIds, setExistingTaskAssetIds] = useState<Set<string>>(new Set());
  const [tasksLoaded, setTasksLoaded] = useState<boolean>(false);
  const theme = useTheme();
  const { fetchInventoryById } = useInventory();
  const { fetchProducts } = useProductsStore();
  const { fetchOutsideInventorySchemas } = useOutsideInventorySchemasStore();
  const { setSnack } = useSnackbar();
  const isInternalUser = isInternalStaff(getUser());

  // Clear any open drawer on initial mount — the drawer should only open when the
  // user clicks a row in the benchmark grid, not from a reloaded or pasted URL.
  useEffect(() => {
    mountedRef.current = true;
    if (search.get('assetDetailId')) {
      const params = new URLSearchParams(search);
      params.delete('assetDetailId');
      setSearch(params, { replace: true });
    }
  }, []);

  useEffect(() => {
    const benchmarkSubscription = getPriceBenchmarkSubject().subscribe((data) => {
      setBenchmarks(data);
      const available = data.filter((b) => b.benchmarkStatus === 'available');
      const needsReview = data.filter((b) => b.benchmarkStatus !== 'available');
      const tabData: Record<PriceBenchmarkTabNames, number> = {
        to_market: available.length,
        needs_review: needsReview.length
      };
      setTabs(
        defaultTabs.map((tabName) => ({
          name: tabName,
          count: tabData[tabName]
        }))
      );
      setAllTabs(
        defaultTabs.map((tabName) => ({
          name: tabName,
          count: tabData[tabName]
        }))
      );
      // If loading just finished and there are no available benchmarks, switch to needs_review.
      if (!getPriceBenchmarkLoadingSubject().getValue() && data.length > 0 && available.length === 0) {
        setSelectedTab(1);
      }
    });
    const benchmarkLoadingSubscription = getPriceBenchmarkLoadingSubject().subscribe((loading) => {
      setBenchmarksLoading(loading);
    });
    const settingsSub = getUserPreferencesSubject().subscribe((prefs) => {
      setUserPreferences(prefs.content);
    });

    fetchPriceBenchmarks();
    fetchProducts();
    fetchOutsideInventorySchemas();

    const fetchAndSetTasks = (accountIds: string[]) => {
      setTasksLoaded(false);
      setExistingTaskAssetIds(new Set());
      if (!accountIds?.length) {
        setTasksLoaded(true);
        return;
      }
      getTasks(accountIds).then(({ data, error }) => {
        if (error) return; // leave tasksLoaded false so the CTA stays hidden
        const taskAssetIds = new Set(
          (data ?? [])
            .filter((t) => t.type === 'Cost Intelligence Request' && t.status === 'Open' && t.sourceId)
            .map((t) => t.sourceId!)
        );
        setExistingTaskAssetIds(taskAssetIds);
        setTasksLoaded(true);
      });
    };

    const accountSub = getSelectedAccountsSubject().subscribe((accounts) => {
      const accountIds = (accounts || []).map((a) => `${a.id}`);
      fetchAndSetTasks(accountIds);
    });

    return () => {
      if (benchmarkSubscription) benchmarkSubscription.unsubscribe();
      if (benchmarkLoadingSubscription) benchmarkLoadingSubscription.unsubscribe();
      if (settingsSub) settingsSub.unsubscribe();
      accountSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const sub = getInventoryByIdSubject().subscribe((asset) => setInventoryAsset(asset || null));
    const loadingSub = getInventoryLoadingSubject().subscribe((loading) => setInventoryLoading(loading));
    return () => {
      sub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (assetDetailId) {
      getInventoryByIdSubject().next(undefined);
      fetchInventoryById(assetDetailId);
    }
  }, [assetDetailId]);

  const handleAssetDrawerClose = () => {
    const params = new URLSearchParams(search);
    params.delete('assetDetailId');
    setSearch(params, { replace: true });
  };

  const assetBenchmark = inventoryAsset ? benchmarks.find((b) => b.assetId === inventoryAsset.id) : undefined;
  const benchmarkExtraSection = assetBenchmark ? (
    <BenchmarkDetails
      benchmark={assetBenchmark}
      pricingOnly
    />
  ) : undefined;

  const handleTaskCreated = (assetId: string) => {
    setExistingTaskAssetIds((prev) => new Set(prev).add(assetId));
  };

  useEffect(() => {
    const tabName = defaultTabs[selectedTab] ?? defaultTabs[0];
    const cols = getBenchMarkColumnsByTab(tabName, existingTaskAssetIds, tasksLoaded, handleTaskCreated);
    setColumns(getGridColumns(benchmarks, cols, `price_benchmark_grid_${tabName}`));
  }, [benchmarks, selectedTab, existingTaskAssetIds, tasksLoaded]);

  const handleContactAdvisor = async () => {
    setAdvisorLoading(true);
    const { error } = await contactAdvisor({ contact_reason: 'cost_intelligence' });
    if (error) {
      setAdvisorLoading(false);
      setSnack({ open: true, type: 'error', message: 'Something went wrong. Please try again or contact support.' });
    } else {
      setAdvisorContacted(true);
      setAdvisorLoading(false);
      setSnack({ open: true, type: 'success', message: 'Your advisor has been notified and will reach out soon.' });
    }
  };

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
  };

  const availableBenchmarks = benchmarks.filter((b) => b.benchmarkStatus === BenchmarkStatus.Available);
  const needsReviewBenchmarks = benchmarks.filter((b) => b.benchmarkStatus !== BenchmarkStatus.Available);
  const allNotAvailable =
    benchmarks.length > 0 && benchmarks.every((b) => b.benchmarkStatus === BenchmarkStatus.NotAvailable);
  const showEmptyState = !benchmarksLoading && (benchmarks.length === 0 || (allNotAvailable && !isInternalUser));

  return (
    <PageLayout pageTitle="Cost Intelligence">
      <div className="px-8 py-10 my-8 rounded flex flex-row justify-between items-end align-bottom bg-nav-50">
        <div className="w-2/3">
          <TextBlock
            size="md"
            color="headerYellow"
            className="mb-4">
            Benchmark Your Services with UPSTACK Intelligence
          </TextBlock>
          <TextBlock
            size="sm14"
            color="white">
            See how your Broadband and Dedicated Internet Access costs stack up against the market — UPSTACK
            Intelligence surfaces top supplier recommendations alongside low and high cost alternatives, giving you the
            clarity to make smarter decisions
          </TextBlock>
        </div>
        {!isInternalUser && (
          <div>
            <button
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              onClick={handleContactAdvisor}
              disabled={advisorContacted || advisorLoading}
              data-cy="get-started-button">
              {advisorContacted ? 'Advisor Notified' : 'Contact your Advisor'}
            </button>
          </div>
        )}
      </div>
      {!benchmarksLoading && availableBenchmarks.length > 0 && (
        <MarketComparisonChart
          benchmarks={availableBenchmarks}
          loading={benchmarksLoading}
        />
      )}
      <PageCard
        childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
        title={`DIA Inventory${benchmarksLoading ? '' : ` - ${benchmarks.length.toLocaleString()}`}`}>
        <>
          <Tabs
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#F9F9F9',
              margin: '-20px',
              '.Mui-selected': {
                backgroundColor: 'white',
                color: theme.palette.primary.main
              }
            }}
            TabIndicatorProps={{
              sx: {
                backgroundColor: 'white',
                height: selectedTab <= tabs.length - 1 ? 2 : 0,
                borderRadius: 1
              }
            }}
            value={selectedTab}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(_e, index) => handleTabChange(index)}>
            {tabs.map((tab, index) => (
              <Tab
                sx={{ textTransform: 'none', fontSize: '.8rem', color: 'black' }}
                key={index}
                data-testid={`benchmark-tab-${tab.name}`}
                label={
                  benchmarksLoading
                    ? PriceBenchmarkTabDisplayNames[tab.name]
                    : `${PriceBenchmarkTabDisplayNames[tab.name]} (${tab.count.toLocaleString()})`
                }
              />
            ))}
          </Tabs>
          {allTabs.map((tab, index) => (
            <TabPanel
              key={`${index}`}
              index={index}
              value={selectedTab}>
              {showEmptyState ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <img
                    src="/public/Bill_empty.svg"
                    alt="No benchmark data"
                    className="w-32 h-32"
                  />
                  <p className="text-lg font-medium text-grey-8">Price to Market Not Yet Available</p>
                  <p className="text-sm text-grey-6 text-center max-w-md">
                    As you add inventory the Price to Market will appear for valid services (Broadband and DIA)
                  </p>
                </div>
              ) : (
                <UpstackDataGrid
                  dataCy="price-benchmark-data-grid"
                  columns={columns}
                  gridSettings={userPreferences?.[`price_benchmark_grid_${tab.name}`] as GridPreferences}
                  loadingData={benchmarksLoading}
                  page={`price_benchmark_grid_${tab.name}`}
                  pinnedColumns={{ left: ['details'] }}
                  rows={tab.name === 'needs_review' ? needsReviewBenchmarks : availableBenchmarks}
                  showSearch
                  title={`Benchmark - ${tab.name}`}
                />
              )}
            </TabPanel>
          ))}
        </>
      </PageCard>
      <div className="h-2"></div>
      <Drawer
        show={!!assetDetailId}
        setShow={handleAssetDrawerClose}
        title={getMetaData('inventory').title}
        subtitle={inventoryAsset?.address}
        link={`/inventory/${assetDetailId}`}
        componentName="inventory"
        hiddenAssetSource="Benchmark"
        data={inventoryAsset}>
        {inventoryLoading || !inventoryAsset ? (
          <ComponentSpinner />
        ) : (
          <DetailsDisplay
            data={inventoryAsset}
            componentName="inventory"
            setShow={handleAssetDrawerClose}
            extraSection={benchmarkExtraSection}
          />
        )}
      </Drawer>
    </PageLayout>
  );
};
